/**
 * Delivery schedule for product conversion triggers + cart reminders.
 * Primary source: Zapiet Pickup + Delivery (cart widget #storePickupApp).
 * Fallback: theme settings in #delivery-schedule-config.
 */
(function () {
  'use strict';

  var configCache = null;
  var zapietDelivery = null;
  var zapietTimezone = null;
  var zapietBlockedDates = null;
  var zapietReady = false;
  var zapietLoadFailed = false;
  var zapietInitComplete = false;

  var WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  var ZAPIET_API = 'https://api.zapiet.com/v1.0';

  function getConfig() {
    if (configCache) return configCache;

    var el = document.getElementById('delivery-schedule-config');
    if (!el) return null;

    try {
      configCache = JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[DeliverySchedule] Invalid config JSON', e);
      configCache = null;
    }

    return configCache;
  }

  function getTimeZone() {
    if (zapietTimezone) return zapietTimezone;
    var config = getConfig();
    return (config && config.timezone) || 'Europe/Dublin';
  }

  function getPartsInTimeZone(date, timeZone) {
    var formatter = new Intl.DateTimeFormat('en-IE', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      weekday: 'short'
    });

    var parts = {};
    formatter.formatToParts(date).forEach(function (part) {
      if (part.type !== 'literal') parts[part.type] = part.value;
    });

    return parts;
  }

  function toDateKey(parts) {
    return parts.year + '-' + parts.month + '-' + parts.day;
  }

  function weekdayFromParts(parts) {
    var map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return map[parts.weekday] != null ? map[parts.weekday] : 0;
  }

  function parseDateKey(key) {
    var bits = key.split('-').map(Number);
    return new Date(bits[0], bits[1] - 1, bits[2]);
  }

  function addDaysToKey(key, days) {
    var timeZone = getTimeZone();
    var cursor = findUtcForWallTime(key, 12, 0, timeZone);

    for (var i = 0; i < days; i++) {
      cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000);
    }

    return toDateKey(getPartsInTimeZone(cursor, timeZone));
  }

  function findUtcForWallTime(dateKey, hour, minute, timeZone) {
    var targetHour = String(hour).padStart(2, '0');
    var targetMinute = String(minute).padStart(2, '0');
    var start = Date.UTC(
      parseInt(dateKey.slice(0, 4), 10),
      parseInt(dateKey.slice(5, 7), 10) - 1,
      parseInt(dateKey.slice(8, 10), 10),
      0,
      0,
      0
    );

    for (var offset = 0; offset <= 48 * 60; offset++) {
      var forward = new Date(start + offset * 60000);
      var backward = new Date(start - offset * 60000);
      var candidates = offset === 0 ? [forward] : [forward, backward];

      for (var i = 0; i < candidates.length; i++) {
        var parts = getPartsInTimeZone(candidates[i], timeZone);
        if (toDateKey(parts) === dateKey && parts.hour === targetHour && parts.minute === targetMinute) {
          return candidates[i];
        }
      }
    }

    return new Date(start + 14 * 3600000);
  }

  function getWeekdayForKey(dateKey, timeZone) {
    var noon = findUtcForWallTime(dateKey, 12, 0, timeZone);
    return weekdayFromParts(getPartsInTimeZone(noon, timeZone));
  }

  function parseBreakpoint(breakpoint) {
    if (!breakpoint || typeof breakpoint !== 'string') return null;
    var bits = breakpoint.split(':');
    return {
      hour: parseInt(bits[0], 10),
      minute: parseInt(bits[1] || '0', 10)
    };
  }

  function uniqueDates(list) {
    var seen = {};
    var out = [];
    (list || []).forEach(function (d) {
      if (d && !seen[d]) {
        seen[d] = true;
        out.push(d);
      }
    });
    return out;
  }

  function mergeZapietBlockedDates(newDates) {
    if (!newDates || !newDates.length) return;
    zapietBlockedDates = uniqueDates((zapietBlockedDates || []).concat(newDates));
  }

  function zapietHasScheduleData() {
    return !!(zapietDelivery || (zapietBlockedDates && zapietBlockedDates.length));
  }

  function collectZapietBlockedDates(delivery) {
    if (!delivery) return [];
    var dates = [].concat(delivery.blackouts || []);

    WEEKDAY_NAMES.forEach(function (name) {
      var day = delivery[name];
      if (day && day.blackout_dates) {
        dates = dates.concat(day.blackout_dates);
      }
    });

    return uniqueDates(dates);
  }

  function getZapietDayConfig(delivery, weekday) {
    if (!delivery) return null;
    return delivery[WEEKDAY_NAMES[weekday]] || null;
  }

  function isZapietDayOpen(delivery, weekday) {
    var day = getZapietDayConfig(delivery, weekday);
    if (!day) return true;
    if (day.slots && day.slots.enabled === false) return false;
    if (day.slots && day.slots.enabled === true) return true;
    if (day.slots && day.slots.slots && day.slots.slots.length > 0) return true;
    if (day.useBreakpoint || day.breakpoint) return true;
    return true;
  }

  function isZapietDateBlocked(dateKey, delivery) {
    var blocked = zapietBlockedDates;
    if (!blocked || !blocked.length) {
      blocked = delivery ? collectZapietBlockedDates(delivery) : [];
    }
    if (!blocked.length) return false;
    return blocked.indexOf(dateKey) !== -1;
  }

  function isZapietDeliverableDateKey(dateKey, delivery, timeZone) {
    var weekday = getWeekdayForKey(dateKey, timeZone);
    if (!isZapietDayOpen(delivery, weekday)) return false;
    if (isZapietDateBlocked(dateKey, delivery)) return false;
    return true;
  }

  function getZapietCutoffForWeekday(delivery, weekday) {
    var day = getZapietDayConfig(delivery, weekday);
    if (!day) return null;

    if (day.useBreakpoint && day.breakpoint) {
      return parseBreakpoint(day.breakpoint);
    }

    return null;
  }

  function getZapietNextDeliverableDateKey(fromKey, delivery, timeZone, maxLookahead) {
    maxLookahead = maxLookahead || 60;
    var key = fromKey;

    for (var i = 0; i < maxLookahead; i++) {
      if (isZapietDeliverableDateKey(key, delivery, timeZone)) return key;
      key = addDaysToKey(key, 1);
    }

    return null;
  }

  function applyZapietLocation(location) {
    if (!location) return;

    if (location.timezone) zapietTimezone = location.timezone;
    if (location.delivery) {
      zapietDelivery = location.delivery;
      mergeZapietBlockedDates(collectZapietBlockedDates(location.delivery));
    }

    zapietReady = true;
    zapietLoadFailed = false;
    notifyScheduleChange();
  }

  function applyZapietShippingDates(data) {
    if (!data) return;
    if (data.timezone) zapietTimezone = data.timezone;

    if (data.disabled && data.disabled.length) {
      var fromCalendar = data.disabled
        .map(function (entry) {
          if (!Array.isArray(entry) || entry.length < 3) return null;
          var y = entry[0];
          var m = String(entry[1]).padStart(2, '0');
          var d = String(entry[2]).padStart(2, '0');
          return y + '-' + m + '-' + d;
        })
        .filter(Boolean);

      mergeZapietBlockedDates(fromCalendar);
      notifyScheduleChange();
    }
  }

  function applyZapietSettings(settings) {
    if (!settings) return;

    if (settings.shipping && settings.shipping.timezone) {
      zapietTimezone = settings.shipping.timezone;
    }

    if (settings.shipping && settings.shipping.blackout_dates) {
      mergeZapietBlockedDates(settings.shipping.blackout_dates);
      notifyScheduleChange();
    }
  }

  function extractLocationFromPayload(payload) {
    if (!payload) return null;
    if (payload.delivery) return payload;
    if (payload.location && payload.location.delivery) return payload.location;
    if (Array.isArray(payload) && payload[0] && payload[0].delivery) return payload[0];
    return null;
  }

  function getShoppingCartFromPage(config) {
    var items = [];

    if (config && config.defaultVariantId) {
      items.push({
        variant_id: config.defaultVariantId,
        product_id: config.defaultProductId || undefined,
        quantity: 1
      });
    }

    document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(function (input) {
      if (input.value && !input.disabled) {
        items.push({
          variant_id: parseInt(input.value, 10),
          quantity: 1
        });
      }
    });

    if (!items.length) return null;
    return items;
  }

  function zapietFetch(path, options) {
    var config = getConfig();
    if (!config || !config.shop) return Promise.reject(new Error('Missing shop'));

    var url = ZAPIET_API + path + (path.indexOf('?') > -1 ? '&' : '?') + 'shop=' + encodeURIComponent(config.shop);
    return fetch(url, options).then(function (response) {
      if (!response.ok) throw new Error('Zapiet API ' + response.status);
      return response.json();
    });
  }

  function loadZapietDeliveryLocation() {
    var config = getConfig();
    if (!config || !config.useZapiet) return Promise.resolve();

    var cart = getShoppingCartFromPage(config);
    if (!cart) return Promise.resolve();

    return zapietFetch('/delivery/locations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        geoSearchQuery: config.geoSearchQuery || 'Dublin, Ireland',
        shoppingCart: cart
      })
    })
      .then(function (location) {
        if (location && location.error) throw new Error(location.error.message || 'No location');
        applyZapietLocation(location);
      })
      .catch(function (err) {
        console.warn('[DeliverySchedule] Zapiet delivery locations:', err);
        zapietLoadFailed = true;
      });
  }

  function loadZapietSettings() {
    return zapietFetch('/settings').then(applyZapietSettings).catch(function (err) {
      console.warn('[DeliverySchedule] Zapiet settings:', err);
    });
  }

  function loadZapietShippingCalendar() {
    var config = getConfig();
    var cart = getShoppingCartFromPage(config);
    if (!cart) return Promise.resolve();

    return zapietFetch('/shipping/calendar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ shoppingCart: cart })
    })
      .then(applyZapietShippingDates)
      .catch(function () {});
  }

  function shouldUseZapiet() {
    var config = getConfig();
    return !!(config && config.enabled && config.useZapiet);
  }

  function useZapietDeliveryRules() {
    return shouldUseZapiet() && zapietReady && !!zapietDelivery;
  }

  function isDateBlocked(key, config) {
    if (shouldUseZapiet() && isZapietDateBlocked(key, zapietDelivery)) {
      return true;
    }
    var blockedDates = (config && config.blockedDates) || [];
    return blockedDates.indexOf(key) !== -1;
  }

  function isWeekdayBlocked(weekday, config) {
    if (useZapietDeliveryRules()) {
      return !isZapietDayOpen(zapietDelivery, weekday);
    }
    var blockedWeekdays = (config && config.blockedWeekdays) || [];
    return blockedWeekdays.indexOf(weekday) !== -1;
  }

  function isDeliverableDateKey(key, config) {
    var timeZone = getTimeZone();
    var weekday = getWeekdayForKey(key, timeZone);

    if (isDateBlocked(key, config)) return false;

    if (useZapietDeliveryRules()) {
      return isZapietDayOpen(zapietDelivery, weekday);
    }

    if (isWeekdayBlocked(weekday, config)) return false;
    return true;
  }

  function getNextDeliverableDateKey(fromKey, config, maxLookahead) {
    var timeZone = getTimeZone();

    if (useZapietDeliveryRules()) {
      return getZapietNextDeliverableDateKey(fromKey, zapietDelivery, timeZone, maxLookahead);
    }

    maxLookahead = maxLookahead || 60;
    var key = fromKey;

    for (var i = 0; i < maxLookahead; i++) {
      if (isDeliverableDateKey(key, config)) return key;
      key = addDaysToKey(key, 1);
    }

    return null;
  }

  function formatDisplayDate(key, timeZone) {
    var noon = findUtcForWallTime(key, 12, 0, timeZone);
    return new Intl.DateTimeFormat('en-IE', {
      timeZone: timeZone,
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    }).format(noon);
  }

  function getCutoffForToday(config, weekday) {
    if (useZapietDeliveryRules()) {
      var zapietCutoff = getZapietCutoffForWeekday(zapietDelivery, weekday);
      if (zapietCutoff) return zapietCutoff;
    }

    return {
      hour: config.sameDayCutoffHour != null ? config.sameDayCutoffHour : 14,
      minute: config.sameDayCutoffMinute != null ? config.sameDayCutoffMinute : 0
    };
  }

  function getCutoffDate(now, config) {
    var timeZone = getTimeZone();
    var parts = getPartsInTimeZone(now, timeZone);
    var todayKey = toDateKey(parts);
    var weekday = weekdayFromParts(parts);
    var cutoff = getCutoffForToday(config, weekday);

    return findUtcForWallTime(todayKey, cutoff.hour, cutoff.minute, timeZone);
  }

  function formatCutoffTime(config, weekday) {
    var cutoff = getCutoffForToday(config, weekday);
    var hour = cutoff.hour;
    var minute = cutoff.minute;
    var period = hour >= 12 ? 'pm' : 'am';
    var displayHour = hour % 12;
    if (displayHour === 0) displayHour = 12;
    if (minute === 0) return displayHour + period;
    return displayHour + ':' + String(minute).padStart(2, '0') + period;
  }

  function pluralize(value, singular, plural) {
    return value === 1 ? singular : plural;
  }

  function formatDurationLive(ms) {
    var totalSeconds = Math.max(0, Math.floor(ms / 1000));
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    var parts = [];

    if (hours > 0) {
      parts.push(hours + ' ' + (hours === 1 ? 'hour' : 'hours'));
    }
    if (minutes > 0 || hours > 0) {
      parts.push(minutes + ' ' + (minutes === 1 ? 'min' : 'mins'));
    }
    parts.push(seconds + ' ' + (seconds === 1 ? 'sec' : 'sec'));

    return parts.join(' ');
  }

  function splitMessageTemplate(template) {
    var parts = (template || '').split('{{ time }}');
    return {
      before: parts[0] || '',
      after: parts[1] || ''
    };
  }

  function getSameDayWindowStart(config) {
    return {
      hour: config.sameDayWindowStartHour != null ? config.sameDayWindowStartHour : 0,
      minute: config.sameDayWindowStartMinute != null ? config.sameDayWindowStartMinute : 0
    };
  }

  function getCutoffForDateKey(dateKey, config, timeZone) {
    var weekday = getWeekdayForKey(dateKey, timeZone);
    return getCutoffForToday(config, weekday);
  }

  function getDeadlineDate(dateKey, hour, minute, timeZone) {
    return findUtcForWallTime(dateKey, hour, minute, timeZone);
  }

  function isNextCalendarDayDeliverable(todayKey, nextDateKey) {
    return nextDateKey === addDaysToKey(todayKey, 1);
  }

  function buildNextDeliveryState(now, config, targetDate, nextDateKey) {
    if (targetDate - (now || new Date()) <= 0) return null;
    if (!nextDateKey) return null;

    var timeZone = getTimeZone();
    var strings = config.strings || {};
    var todayKey = toDateKey(getPartsInTimeZone(now || new Date(), timeZone));
    var isTomorrow = isNextCalendarDayDeliverable(todayKey, nextDateKey);
    var template;

    if (isTomorrow) {
      template = strings.needItTomorrow || 'Need it tomorrow? Order within {{ time }}.';
    } else {
      template =
        strings.orderWithinForDeliveryOn || 'Order within {{ time }} for delivery on {{ date }}.';
    }

    template = template.replace(/\{\{\s*date\s*\}\}/gi, formatDisplayDate(nextDateKey, timeZone));
    var templateParts = splitMessageTemplate(template);

    return {
      visible: true,
      mode: isTomorrow ? 'tomorrow' : 'next_delivery',
      targetDate: targetDate,
      messageBefore: templateParts.before,
      messageAfter: templateParts.after,
      submessage: null
    };
  }

  function buildSameDayState(now, config, targetDate) {
    if (targetDate - (now || new Date()) <= 0) return null;

    var strings = config.strings || {};
    var templateParts = splitMessageTemplate(
      strings.orderWithinForToday || 'Order within {{ time }} for delivery today'
    );

    return {
      visible: true,
      mode: 'same_day',
      targetDate: targetDate,
      messageBefore: templateParts.before,
      messageAfter: templateParts.after,
      submessage: null
    };
  }

  function getFallbackState(config, weekday, now) {
    var timeZone = getTimeZone();
    var parts = getPartsInTimeZone(now || new Date(), timeZone);
    var todayKey = toDateKey(parts);
    var hour = parseInt(parts.hour, 10);
    var minute = parseInt(parts.minute, 10);
    if (hour === 24) hour = 0;

    var windowStart = getSameDayWindowStart(config);
    var cutoff = getCutoffForToday(config, weekday);
    var nowTotalMinutes = hour * 60 + minute;
    var windowStartTotalMinutes = windowStart.hour * 60 + windowStart.minute;
    var cutoffTotalMinutes = cutoff.hour * 60 + cutoff.minute;

    if (nowTotalMinutes >= windowStartTotalMinutes && nowTotalMinutes < cutoffTotalMinutes) {
      return buildSameDayState(now, config, getCutoffDate(now, config));
    }

    var targetDate;
    var nextKey;
    if (nowTotalMinutes < windowStartTotalMinutes) {
      targetDate = getDeadlineDate(todayKey, windowStart.hour, windowStart.minute, timeZone);
      nextKey = todayKey;
    } else {
      nextKey = getNextDeliverableDateKey(addDaysToKey(todayKey, 1), config) || addDaysToKey(todayKey, 1);
      var nextCutoff = getCutoffForDateKey(nextKey, config, timeZone);
      targetDate = getDeadlineDate(nextKey, nextCutoff.hour, nextCutoff.minute, timeZone);
    }

    return buildNextDeliveryState(now, config, targetDate, nextKey);
  }

  function getScheduleState(now) {
    var config = getConfig();
    if (!config || !config.enabled) {
      return { visible: false, reason: 'disabled' };
    }

    if (shouldUseZapiet() && !zapietInitComplete && !zapietLoadFailed) {
      return {
        visible: true,
        mode: 'loading',
        message: (config.strings && config.strings.zapietLoading) || 'Checking delivery availability…',
        submessage: null
      };
    }

    now = now || new Date();
    var timeZone = getTimeZone();
    var parts = getPartsInTimeZone(now, timeZone);
    var todayKey = toDateKey(parts);
    var weekday = weekdayFromParts(parts);
    var hour = parseInt(parts.hour, 10);
    var minute = parseInt(parts.minute, 10);
    if (hour === 24) hour = 0;

    var strings = config.strings || {};
    var windowStart = getSameDayWindowStart(config);
    var cutoff = getCutoffForToday(config, weekday);
    var windowStartTotalMinutes = windowStart.hour * 60 + windowStart.minute;
    var cutoffTotalMinutes = cutoff.hour * 60 + cutoff.minute;
    var nowTotalMinutes = hour * 60 + minute;
    var todayDeliverable = isDeliverableDateKey(todayKey, config);
    var inSameDayWindow =
      nowTotalMinutes >= windowStartTotalMinutes && nowTotalMinutes < cutoffTotalMinutes;

    if (todayDeliverable && inSameDayWindow) {
      var sameDayState = buildSameDayState(now, config, getCutoffDate(now, config));
      if (sameDayState) return sameDayState;
    }

    if (todayDeliverable) {
      var nextDeliveryTarget;
      var nextDeliveryDateKey;

      if (nowTotalMinutes < windowStartTotalMinutes) {
        nextDeliveryDateKey = todayKey;
        nextDeliveryTarget = getDeadlineDate(todayKey, windowStart.hour, windowStart.minute, timeZone);
      } else if (nowTotalMinutes >= cutoffTotalMinutes) {
        nextDeliveryDateKey =
          getNextDeliverableDateKey(addDaysToKey(todayKey, 1), config) || addDaysToKey(todayKey, 1);
        var nextCutoff = getCutoffForDateKey(nextDeliveryDateKey, config, timeZone);
        nextDeliveryTarget = getDeadlineDate(
          nextDeliveryDateKey,
          nextCutoff.hour,
          nextCutoff.minute,
          timeZone
        );
      }

      if (nextDeliveryTarget && nextDeliveryDateKey) {
        var nextDeliveryState = buildNextDeliveryState(
          now,
          config,
          nextDeliveryTarget,
          nextDeliveryDateKey
        );
        if (nextDeliveryState) return nextDeliveryState;
      }
    }

    if (!todayDeliverable) {
      var nextKey = getNextDeliverableDateKey(todayKey, config);
      if (nextKey) {
        var nextCutoff = getCutoffForDateKey(nextKey, config, timeZone);
        var nextTarget = getDeadlineDate(nextKey, nextCutoff.hour, nextCutoff.minute, timeZone);
        var closedDayState = buildNextDeliveryState(now, config, nextTarget, nextKey);
        if (closedDayState) return closedDayState;
      }
    }

    var fallback = getFallbackState(config, weekday, now);
    if (fallback) return fallback;

    return buildSameDayState(now, config, getCutoffDate(now, config)) || {
      visible: true,
      mode: 'same_day',
      targetDate: getCutoffDate(now, config),
      messageBefore: 'Order within ',
      messageAfter: ' for delivery today',
      submessage: null
    };
  }

  function notifyScheduleChange() {
    document.dispatchEvent(new CustomEvent('delivery-schedule:updated'));
    updateCartReminder();
    document.querySelectorAll('delivery-conversion-trigger').forEach(function (el) {
      if (typeof el.refresh === 'function') el.refresh();
    });
  }

  function bindZapietEvents() {
    if (!window.ZapietEvent || typeof window.ZapietEvent.listen !== 'function') return;

    var events = ['widget_loaded', 'getDeliveryLocations', 'noShippingDates', 'selected_method', 'checkoutEnabled'];

    events.forEach(function (eventName) {
      window.ZapietEvent.listen(eventName, function (payload) {
        if (eventName === 'noShippingDates') {
          if (Array.isArray(payload)) {
            mergeZapietBlockedDates(payload);
            zapietReady = true;
            notifyScheduleChange();
          }
          return;
        }

        var location = extractLocationFromPayload(payload);
        if (location) {
          applyZapietLocation(location);
          return;
        }

        if (eventName === 'widget_loaded') {
          loadZapietDeliveryLocation().then(notifyScheduleChange);
        }
      });
    });
  }

  function waitForZapiet(callback, attempt) {
    attempt = attempt || 0;
    if (window.ZapietEvent) {
      callback();
      return;
    }
    if (attempt > 40) return;
    setTimeout(function () {
      waitForZapiet(callback, attempt + 1);
    }, 250);
  }

  function finishZapietInit() {
    zapietInitComplete = true;
    if (!zapietHasScheduleData()) {
      zapietLoadFailed = true;
    }
    notifyScheduleChange();
  }

  function initZapiet() {
    if (!shouldUseZapiet()) return;

    var loadTimeout = setTimeout(finishZapietInit, 6000);

    Promise.all([loadZapietSettings(), loadZapietShippingCalendar(), loadZapietDeliveryLocation()])
      .then(function () {
        clearTimeout(loadTimeout);
        finishZapietInit();
      })
      .catch(function () {
        clearTimeout(loadTimeout);
        finishZapietInit();
      });

    waitForZapiet(bindZapietEvents);
  }

  function getCountdownLabels() {
    var strings = (getConfig() && getConfig().strings) || {};
    return {
      hours: strings.countdownHours || 'Hours',
      minutes: strings.countdownMinutes || 'Minutes',
      seconds: strings.countdownSeconds || 'Seconds'
    };
  }

  function mountDeliveryCountdown(host, cutoffDate) {
    if (!host || !cutoffDate) return null;
    if (cutoffDate <= new Date()) return null;

    var iso = cutoffDate.toISOString();
    var existing = host.querySelector('countdown-timer');

    if (existing && existing.getAttribute('expires-at') === iso) {
      existing.hidden = false;
      return existing;
    }

    var labels = getCountdownLabels();
    host.innerHTML =
      '<countdown-timer class="delivery-conversion-trigger__countdown" role="timer" expires-at="' +
      iso +
      '">' +
      '<div class="h-stack gap-2 justify-center">' +
      '<div class="delivery-conversion-trigger__countdown-item v-stack gap-1 align-center">' +
      '<countdown-timer-flip type="hours" class="delivery-conversion-trigger__flip h5">00</countdown-timer-flip>' +
      '<span class="text-xs text-subdued">' +
      labels.hours +
      '</span></div>' +
      '<span aria-hidden="true" class="h5">:</span>' +
      '<div class="delivery-conversion-trigger__countdown-item v-stack gap-1 align-center">' +
      '<countdown-timer-flip type="minutes" class="delivery-conversion-trigger__flip h5">00</countdown-timer-flip>' +
      '<span class="text-xs text-subdued">' +
      labels.minutes +
      '</span></div>' +
      '<span aria-hidden="true" class="h5">:</span>' +
      '<div class="delivery-conversion-trigger__countdown-item v-stack gap-1 align-center">' +
      '<countdown-timer-flip type="seconds" class="delivery-conversion-trigger__flip h5">00</countdown-timer-flip>' +
      '<span class="text-xs text-subdued">' +
      labels.seconds +
      '</span></div></div></countdown-timer>';

    return host.querySelector('countdown-timer');
  }

  function clearDeliveryCountdown(host) {
    if (host) host.innerHTML = '';
  }

  function updateCartReminder() {
    var config = getConfig();
    if (!config || !config.enabled) return;

    var el = document.querySelector('[data-delivery-cart-reminder]');
    if (!el) return;

    var state = getScheduleState(new Date());
    var strings = config.strings || {};
    var parts = getPartsInTimeZone(new Date(), getTimeZone());
    var cutoffLabel = formatCutoffTime(config, weekdayFromParts(parts));

    if (state.mode === 'same_day') {
      el.hidden = false;
      el.textContent = (strings.cartSameDayReminder || 'Same-day delivery is available when you complete your order before {{ cutoff }}.').replace(
        '{{ cutoff }}',
        cutoffLabel
      );
    } else if (state.mode === 'loading') {
      el.hidden = false;
      el.textContent = state.message;
    } else {
      el.hidden = true;
    }
  }

  class DeliveryConversionTrigger extends HTMLElement {
    connectedCallback() {
      this.messageEl = this.querySelector('[data-delivery-message]');
      this.beforeEl = this.messageEl && this.messageEl.querySelector('[data-delivery-before]');
      this.timerEl = this.messageEl && this.messageEl.querySelector('[data-delivery-timer]');
      this.afterEl = this.messageEl && this.messageEl.querySelector('[data-delivery-after]');
      this.partsInitialized = !!(this.beforeEl && this.timerEl && this.afterEl);
      this.lastMode = null;

      this.refresh = this.refresh.bind(this);
      this.refresh();
      this.interval = setInterval(this.refresh, 1000);
      document.addEventListener('delivery-schedule:updated', this.refresh);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      document.removeEventListener('delivery-schedule:updated', this.refresh);
    }

    resetMessageParts() {
      this.partsInitialized = false;
      this.beforeEl = null;
      this.timerEl = null;
      this.afterEl = null;
    }

    ensureMessageParts() {
      if (!this.messageEl || this.partsInitialized) return;

      this.messageEl.innerHTML =
        '<span data-delivery-before></span>' +
        '<span class="delivery-conversion-trigger__time" data-delivery-timer aria-live="polite"></span>' +
        '<span data-delivery-after></span>';
      this.beforeEl = this.messageEl.querySelector('[data-delivery-before]');
      this.timerEl = this.messageEl.querySelector('[data-delivery-timer]');
      this.afterEl = this.messageEl.querySelector('[data-delivery-after]');
      this.partsInitialized = true;
    }

    refresh() {
      var config = getConfig();
      if (!config || !config.enabled) {
        return;
      }

      var state = getScheduleState(new Date());

      if (!state.visible || !this.messageEl) {
        return;
      }

      if (state.mode !== this.lastMode) {
        this.resetMessageParts();
        this.lastMode = state.mode;
      }

      if (!state.targetDate) {
        this.messageEl.textContent = state.message || '';
        return;
      }

      this.ensureMessageParts();

      if (this.beforeEl.textContent !== state.messageBefore) {
        this.beforeEl.textContent = state.messageBefore;
      }
      if (this.afterEl.textContent !== state.messageAfter) {
        this.afterEl.textContent = state.messageAfter;
      }

      var remaining = state.targetDate - Date.now();
      this.timerEl.textContent = formatDurationLive(remaining);
    }
  }

  window.DeliverySchedule = {
    getConfig: getConfig,
    getScheduleState: getScheduleState,
    updateCartReminder: updateCartReminder,
    refreshZapiet: function () {
      return loadZapietDeliveryLocation().then(notifyScheduleChange);
    }
  };

  if (!window.customElements.get('delivery-conversion-trigger')) {
    window.customElements.define('delivery-conversion-trigger', DeliveryConversionTrigger);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initZapiet();
    updateCartReminder();
    setInterval(updateCartReminder, 60000);
  });

  document.addEventListener('change', function (event) {
    if (!shouldUseZapiet()) return;
    if (event.target && event.target.name === 'id') {
      loadZapietDeliveryLocation().then(notifyScheduleChange);
    }
  });
})();
